import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { getApiUrl } from '@/lib/utils';
import { ChevronDown, ChevronRight } from 'lucide-react';

const API = getApiUrl();

// Color palette for category accent bars (restored original colors)
const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042', '#8884D8', '#82ca9d'];

export const ExpensesPage = ({ user, t, formatCurrency, buildingId = null }) => {
  const navigate = useNavigate();
  const [expenses, setExpenses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showModal, setShowModal] = useState(false);
  const [expandedCategories, setExpandedCategories] = useState({});
  const [formData, setFormData] = useState({ 
    category: '', 
    subcategory: '', 
    amount: 0, 
    date: new Date().toISOString().split('T')[0], 
    description: '' 
  });

  const fetchExpenses = useCallback(async () => {
    try {
      const url = buildingId ? `${API}/expenses?building_id=${buildingId}` : `${API}/expenses`;
      const response = await axios.get(url, { withCredentials: true });
      setExpenses(response.data);
      
      // Default: ALL categories collapsed
      setExpandedCategories({});
    } catch (error) {
      toast.error(t('loadDataFailed'));
    } finally {
      setLoading(false);
    }
  }, [buildingId, t]);

  useEffect(() => {
    fetchExpenses();
  }, [fetchExpenses]);

  const handleCreate = async (e) => {
    e.preventDefault();
    
    if (!formData.category) {
      toast.error(t('categoryRequired'));
      return;
    }
    if (!formData.subcategory) {
      toast.error(t('subcategoryRequired'));
      return;
    }
    
    try {
      const data = { ...formData, date: new Date(formData.date).toISOString() };
      const url = buildingId ? `${API}/expenses?building_id=${buildingId}` : `${API}/expenses`;
      await axios.post(url, data, { withCredentials: true });
      toast.success(t('expenseCreated'));
      setShowModal(false);
      setFormData({ category: '', subcategory: '', amount: 0, date: new Date().toISOString().split('T')[0], description: '' });
      fetchExpenses();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  const handleDelete = async (expenseId) => {
    if (!window.confirm(t('deleteExpense'))) return;
    try {
      const url = buildingId 
        ? `${API}/expenses/${expenseId}?building_id=${buildingId}`
        : `${API}/expenses/${expenseId}`;
      await axios.delete(url, { withCredentials: true });
      toast.success(t('expenseDeleted'));
      fetchExpenses();
    } catch (error) {
      toast.error(t('operationFailed'));
    }
  };

  // Memoized calculations
  const { groupedExpenses, grandTotal, totalItems } = useMemo(() => {
    // Calculate grand total
    const total = expenses.reduce((sum, exp) => sum + exp.amount, 0);
    
    // Group by category
    const categoryTotals = expenses.reduce((acc, expense) => {
      const cat = expense.category || 'other';
      if (!acc[cat]) {
        acc[cat] = { total: 0, count: 0, items: [] };
      }
      acc[cat].total += expense.amount;
      acc[cat].count += 1;
      acc[cat].items.push(expense);
      return acc;
    }, {});
    
    // Prepare grouped expenses sorted by total desc, items by date desc
    const grouped = Object.entries(categoryTotals)
      .map(([category, data]) => ({
        category,
        total: data.total,
        count: data.count,
        items: data.items.sort((a, b) => new Date(b.date) - new Date(a.date))
      }))
      .sort((a, b) => b.total - a.total);
    
    return {
      groupedExpenses: grouped,
      grandTotal: total,
      totalItems: expenses.length
    };
  }, [expenses]);

  // Category options
  const getCategoryOptions = () => [
    { key: 'commonArea', label: t('expenseCategories.commonArea') },
    { key: 'maintenance', label: t('expenseCategories.maintenance') },
    { key: 'management', label: t('expenseCategories.management') },
    { key: 'taxOfficial', label: t('expenseCategories.taxOfficial') },
    { key: 'insurance', label: t('expenseCategories.insurance') },
    { key: 'other', label: t('expenseCategories.other') }
  ];

  // Subcategory options
  const getSubcategoryOptions = (categoryKey) => {
    if (!categoryKey) return [];
    
    const subcategoriesMap = {
      commonArea: ['cleaning', 'security', 'janitor', 'reception', 'gardening', 'elevatorMaintenance', 'generatorMaintenance', 'fireSystemsMaintenance', 'poolMaintenance', 'commonElectricity', 'commonWater', 'naturalGas'],
      maintenance: ['electricalRepair', 'plumbingRepair', 'elevatorBreakdown', 'roofRepair', 'paintingRenovation', 'fixtureRepair', 'emergencyRepairs'],
      management: ['managerFee', 'accountingAdvisor', 'buildingManagementService', 'legal', 'consultancy', 'stationeryOffice', 'softwareSystem'],
      taxOfficial: ['taxes', 'municipalityFees', 'licenseProcedures', 'penaltiesFees'],
      insurance: ['buildingInsurance', 'assetInsurance', 'occupationalHealth', 'fireExtinguisher'],
      other: ['pestControl', 'wasteManagement', 'snowRemoval', 'generalExpense', 'other']
    };

    const subcategoryKeys = subcategoriesMap[categoryKey] || [];
    return subcategoryKeys.map(key => ({
      key: key,
      label: t(`expenseSubcategories.${categoryKey}.${key}`)
    }));
  };

  // Translate category key (memoized)
  const translateCategory = useCallback((categoryKey) => {
    if (!categoryKey) return '';
    const translatedKey = t(`expenseCategories.${categoryKey}`);
    if (translatedKey === `expenseCategories.${categoryKey}`) {
      return categoryKey;
    }
    return translatedKey;
  }, [t]);

  // Translate subcategory key (memoized)
  const translateSubcategory = useCallback((categoryKey, subcategoryKey) => {
    if (!categoryKey || !subcategoryKey) return '';
    const translatedKey = t(`expenseSubcategories.${categoryKey}.${subcategoryKey}`);
    if (translatedKey === `expenseSubcategories.${categoryKey}.${subcategoryKey}`) {
      return subcategoryKey;
    }
    return translatedKey;
  }, [t]);

  // Toggle category expansion
  const toggleCategory = (category) => {
    setExpandedCategories(prev => ({
      ...prev,
      [category]: !prev[category]
    }));
  };

  // Expand/collapse all
  const toggleAll = (expand) => {
    const newState = {};
    groupedExpenses.forEach(group => {
      newState[group.category] = expand;
    });
    setExpandedCategories(newState);
  };

  // Check if all are expanded
  const allExpanded = useMemo(() => {
    return groupedExpenses.length > 0 && groupedExpenses.every(g => expandedCategories[g.category]);
  }, [groupedExpenses, expandedCategories]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-teal-500"></div>
      </div>
    );
  }

  const isAdmin = user?.role === 'admin' || user?.role === 'superadmin';

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-gray-800 text-white sticky top-0 z-50 shadow-md">
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <button onClick={() => navigate('/app')} className="text-gray-200 hover:text-white">
                ← {t('back')}
              </button>
              <h1 className="text-xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('expenses')}</h1>
            </div>
            {isAdmin && (
              <button
                data-testid="create-expense-button"
                onClick={() => setShowModal(true)}
                className="px-4 py-2 bg-teal-600 text-white rounded-xl hover:bg-teal-700 transition-colors font-semibold"
              >
                {t('newExpense')}
              </button>
            )}
          </div>
        </div>
      </header>

      <div className="container mx-auto px-4 py-6 max-w-4xl">
        {/* Single Section: Gider Raporu (Expense Report) */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {t('expenseReport')}
            </h2>
            {groupedExpenses.length > 0 && (
              <div className="flex gap-2">
                <button
                  onClick={() => toggleAll(true)}
                  className={`text-xs px-3 py-1 rounded-lg transition-colors ${
                    allExpanded 
                      ? 'bg-teal-100 text-teal-700' 
                      : 'text-teal-600 hover:bg-teal-50'
                  }`}
                >
                  {t('expandAll')}
                </button>
                <button
                  onClick={() => toggleAll(false)}
                  className={`text-xs px-3 py-1 rounded-lg transition-colors ${
                    !allExpanded && groupedExpenses.length > 0
                      ? 'bg-gray-200 text-gray-700' 
                      : 'text-gray-600 hover:bg-gray-100'
                  }`}
                >
                  {t('collapseAll')}
                </button>
              </div>
            )}
          </div>
          
          {groupedExpenses.length > 0 ? (
            <>
              {/* Category List */}
              <div className="space-y-3">
                {groupedExpenses.map((group, groupIndex) => (
                  <div key={group.category} className="border rounded-xl overflow-hidden">
                    {/* Category Header - Accordion */}
                    <button
                      onClick={() => toggleCategory(group.category)}
                      className="w-full flex items-center justify-between p-4 bg-gray-50 hover:bg-gray-100 transition-colors"
                      style={{ borderLeftWidth: '4px', borderLeftColor: COLORS[groupIndex % COLORS.length] }}
                    >
                      <div className="flex items-center gap-3">
                        {expandedCategories[group.category] ? (
                          <ChevronDown className="w-5 h-5 text-gray-500" />
                        ) : (
                          <ChevronRight className="w-5 h-5 text-gray-500" />
                        )}
                        <div className="text-left">
                          <h3 className="font-bold text-gray-800" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                            {translateCategory(group.category)}
                          </h3>
                          <p className="text-xs text-gray-500">
                            {group.count} {group.count === 1 ? t('itemCount') : t('items')}
                          </p>
                        </div>
                      </div>
                      <span className="text-lg font-bold text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                        {formatCurrency(group.total)}
                      </span>
                    </button>
                    
                    {/* Category Items - Collapsible */}
                    {expandedCategories[group.category] && (
                      <div className="divide-y">
                        {group.items.map((expense) => (
                          <div 
                            key={expense.id} 
                            className="p-4 hover:bg-gray-50 transition-colors"
                            data-testid={`expense-${expense.id}`}
                          >
                            <div className="flex justify-between items-start">
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <div className="flex-1">
                                    {expense.subcategory && (
                                      <p className="text-sm font-medium text-gray-800">
                                        {translateSubcategory(expense.category, expense.subcategory)}
                                      </p>
                                    )}
                                    {expense.description && (
                                      <p className="text-sm text-gray-600 mt-1" style={{ fontFamily: 'Inter, sans-serif' }}>
                                        {expense.description}
                                      </p>
                                    )}
                                  </div>
                                  <span className="text-base font-bold text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                                    {formatCurrency(expense.amount)}
                                  </span>
                                </div>
                                <div className="flex items-center gap-4 text-xs text-gray-500 mt-2">
                                  <span>📅 {new Date(expense.date).toLocaleDateString()}</span>
                                  {expense.created_by_name && (
                                    <span>👤 {expense.created_by_name}</span>
                                  )}
                                </div>
                              </div>
                              {isAdmin && (
                                <button
                                  onClick={() => handleDelete(expense.id)}
                                  className="ml-4 px-3 py-1 text-sm text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                                  data-testid={`delete-expense-${expense.id}`}
                                >
                                  {t('delete')}
                                </button>
                              )}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {/* Grand Total Summary - Matches old design style */}
              <div className="mt-6 pt-4 border-t-2 border-gray-200">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className="text-sm font-semibold text-gray-600">{t('grandTotalExpense')}</span>
                    <span className="text-xs text-gray-400">
                      ({totalItems} {totalItems === 1 ? t('itemCount') : t('items')})
                    </span>
                  </div>
                  <span className="text-xl font-bold text-red-600" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                    {formatCurrency(grandTotal)}
                  </span>
                </div>
              </div>
            </>
          ) : (
            <div className="text-center py-12 text-gray-500">
              <p className="text-lg mb-2">{t('noExpensesYet')}</p>
              {isAdmin && (
                <p className="text-sm">{t('newExpense')} {t('addFirstExpense')}</p>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Create Expense Modal */}
      {showModal && isAdmin && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl p-8 max-w-md w-full" data-testid="create-expense-modal">
            <h2 className="text-2xl font-bold mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>{t('newExpense')}</h2>
            <form onSubmit={handleCreate}>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('categoryLabel')}</label>
                <select
                  data-testid="category-select"
                  value={formData.category}
                  onChange={(e) => setFormData({ ...formData, category: e.target.value, subcategory: '' })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  required
                >
                  <option value="">{t('categoryPlaceholder')}</option>
                  {getCategoryOptions().map(cat => (
                    <option key={cat.key} value={cat.key}>{cat.label}</option>
                  ))}
                </select>
              </div>
              
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('subcategoryLabel')}</label>
                <select
                  data-testid="subcategory-select"
                  value={formData.subcategory}
                  onChange={(e) => setFormData({ ...formData, subcategory: e.target.value })}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
                  disabled={!formData.category}
                  required
                >
                  <option value="">{t('subcategoryPlaceholder')}</option>
                  {getSubcategoryOptions(formData.category).map(subcat => (
                    <option key={subcat.key} value={subcat.key}>{subcat.label}</option>
                  ))}
                </select>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('amount')}</label>
                <input
                  type="number"
                  data-testid="expense-amount-input"
                  value={formData.amount}
                  onChange={(e) => setFormData({ ...formData, amount: parseFloat(e.target.value) })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-4">
                <label className="block text-sm font-semibold mb-2">{t('date')}</label>
                <input
                  type="date"
                  data-testid="date-input"
                  value={formData.date}
                  onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  required
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-semibold mb-2">{t('description')}</label>
                <textarea
                  data-testid="description-input"
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full px-4 py-2 border rounded-lg"
                  rows="3"
                  required
                />
              </div>
              <div className="flex gap-4">
                <button type="button" onClick={() => setShowModal(false)} className="flex-1 px-4 py-2 border rounded-lg">{t('cancel')}</button>
                <button 
                  type="submit" 
                  data-testid="submit-expense" 
                  disabled={!formData.category || !formData.subcategory || !formData.amount}
                  className={`flex-1 px-4 py-2 rounded-lg ${
                    !formData.category || !formData.subcategory || !formData.amount
                      ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                      : 'bg-teal-600 text-white hover:bg-teal-700'
                  }`}
                >
                  {t('create')}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
